"use client";

import React from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUiLanguageOptions } from "@/hooks/use-ui-language-options";
import { getUiLanguageOptionByLocale } from "@/shared/i18n/languages";
import { getUiLocaleDirection } from "@/lib/i18n/uiLocale";
import { cn } from "@/src/lib/utils";

interface LanguageDropdownProps {
  current: string;
  onChange: (locale: string) => void;
  align?: "start" | "center" | "end";
  className?: string;
}

export function LanguageDropdown({ current, onChange, align = "end", className }: LanguageDropdownProps) {
  const options = useUiLanguageOptions();
  const active = getUiLanguageOptionByLocale(options, current);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Language"
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50",
            className,
          )}
        >
          <Globe className="h-4 w-4 text-gray-500" />
          <span dir={getUiLocaleDirection(active?.locale ?? current)}>{active?.label ?? current.toUpperCase()}</span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[180px] max-h-[60vh] overflow-y-auto">
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.locale}
            onSelect={() => onChange(opt.locale)}
            className="cursor-pointer justify-between gap-3"
          >
            <span dir={getUiLocaleDirection(opt.locale)} className="text-sm">{opt.label}</span>
            {opt.locale === active?.locale ? <Check className="h-4 w-4 text-blue-600" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
